'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var graphqlTag = require('graphql-tag');
var client = require('@apollo/client');
var zod = require('zod');
var nthHookContextAnalytics = require('@narvar/nth-hook-context-analytics');
var nthHookTheme = require('@narvar/nth-hook-theme');
var nthHookContextPage = require('@narvar/nth-hook-context-page');
var CustomComponent_module = require('./CustomComponent.module.css.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

const PING_INTERVAL_MS = 300;
const customComponentTrackingEventSchema = zod.z.object({
  eventType: zod.z.union([zod.z.literal(nthHookContextAnalytics.NarvarEventTypes.COMPONENT_RENDER), zod.z.literal(nthHookContextAnalytics.NarvarEventTypes.CLICK)]),
  clickSubType: zod.z.string().optional()
});
const CustomComponent = ({
  featureName,
  componentId,
  guestComponentSettings
}) => {
  const {
    panelcontainerNoneNone: {
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius
    } = {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0
    }
  } = nthHookTheme.useTheme();
  const {
    state
  } = nthHookContextPage.usePageContext();
  const [guestReadyForMessages, setGuestReady] = React.useState(false);
  const [guestIsLoaded, setGuestContentLoaded] = React.useState(false);
  const [iFrameHeight, setIFrameHeight] = React.useState('0');
  const pingGuestInterval = React.useRef();
  const logEvent = nthHookContextAnalytics.useAnalyticsPost();
  const {
    graphqlQuery,
    url
  } = guestComponentSettings || {
    graphqlQuery: '',
    url: ''
  };
  const guestAppUrl = url || '';
  const query = graphqlQuery ? graphqlTag.gql(graphqlQuery) : graphqlTag.gql('query exampleQuery { exampleProperty }');
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const {
    data
  } = client.useQuery(query, {
    variables: state,
    skip: !graphqlQuery
  });
  const [iFrameRef, updateIFrameRef] = React.useState(null);
  const baseLogInfo = {
    eventComponent: nthHookContextAnalytics.NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
    eventFeature: nthHookContextAnalytics.NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
    feature: featureName,
    pageComponent: nthHookContextAnalytics.NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT
  };
  React.useEffect(() => {
    const receiveMessage = ({
      data: {
        guestReadyToReceiveMessages,
        guestContentLoaded,
        guestDocumentSize,
        parentWindowId,
        trackEvent
      },
      origin
    }) => {
      if (!guestAppUrl.includes(origin) || parentWindowId !== componentId) return;
      if (guestReadyToReceiveMessages) setGuestReady(true);
      if (guestContentLoaded) setGuestContentLoaded(true);
      if (guestDocumentSize) setIFrameHeight(guestDocumentSize);
      if (trackEvent !== undefined) {
        try {
          const eventValidated = customComponentTrackingEventSchema.parse(trackEvent);
          logEvent({
            ...baseLogInfo,
            eventType: eventValidated.eventType,
            clickSubType: eventValidated.clickSubType
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e instanceof Error ? `Invalid tracking event: ${e.message}` : 'Invalid tracking event');
        }
      }
    };
    window.addEventListener('message', receiveMessage);
    return () => window.removeEventListener('message', receiveMessage);
  },
  // TODO: Make this dependency array exhaustive
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [guestAppUrl, componentId]);
  React.useEffect(() => {
    if (iFrameRef === null) return undefined;
    const {
      contentWindow: hostIFrameRef
    } = iFrameRef;
    if (!hostIFrameRef) return undefined;
    if (!guestReadyForMessages) {
      const ping = () => {
        try {
          hostIFrameRef.postMessage({
            type: 'windowId',
            data: {
              parentWindowId: componentId
            }
          }, guestAppUrl);
        } catch (e) {
          if (e instanceof Error) {
            // eslint-disable-next-line no-console
            console.error('Custom Component - error in ping postMessage');
            // eslint-disable-next-line no-console
            console.table({
              errorMessage: e.message,
              featureName,
              componentId
            });
          } else {
            // eslint-disable-next-line no-console
            console.error('Custom Component - error in ping postMessage');
            // eslint-disable-next-line no-console
            console.table({
              featureName,
              componentId
            });
          }
        }
      };
      ping();
      pingGuestInterval.current = window.setInterval(ping, PING_INTERVAL_MS);
      return () => window.clearInterval(pingGuestInterval.current);
    }
    window.clearInterval(pingGuestInterval.current);
    try {
      hostIFrameRef.postMessage({
        type: 'settings',
        data: {
          pageContext: state,
          settings: guestComponentSettings && guestComponentSettings.settings || {},
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          queryData: data || {}
        }
      }, guestAppUrl);
    } catch (e) {
      if (e instanceof Error) {
        // eslint-disable-next-line no-console
        console.error('Custom Component - error in settings postMessage');
        // eslint-disable-next-line no-console
        console.table({
          errorMessage: e.message,
          featureName,
          componentId
        });
      } else {
        // eslint-disable-next-line no-console
        console.error('Custom Component - error in settings postMessage');
        // eslint-disable-next-line no-console
        console.table({
          featureName,
          componentId
        });
      }
    }
    return undefined;
  },
  // TODO: Make this dependency array exhaustive
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [iFrameRef, componentId, data, guestAppUrl, guestComponentSettings, guestReadyForMessages, state]);
  React.useEffect(() => {
    if (!guestIsLoaded) return;
    logEvent({
      ...baseLogInfo,
      eventType: nthHookContextAnalytics.NarvarEventTypes.COMPONENT_RENDER
    });
  },
  // TODO: Make this dependency array exhaustive
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [guestIsLoaded]);
  if (guestComponentSettings === null || guestComponentSettings === undefined || guestComponentSettings.url === null || guestComponentSettings.url === undefined) return null;
  return /*#__PURE__*/React__default["default"].createElement("iframe", {
    ref: ref => updateIFrameRef(ref),
    className: CustomComponent_module["default"].iframe
    // TODO: Set the title of this with a translation
    ,
    title: "iFrameComponent",
    src: guestAppUrl,
    sandbox: "allow-popups allow-scripts allow-same-origin allow-forms",
    style: {
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius,
      display: guestIsLoaded ? 'block' : 'none',
      height: iFrameHeight
    }
  });
};
var CustomComponent$1 = nthHookTheme.withThemeProvider(CustomComponent, {
  themeTokens: ['panelcontainerNoneNone']
});

exports.PING_INTERVAL_MS = PING_INTERVAL_MS;
exports["default"] = CustomComponent$1;
